import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { CoinIcon, FeesSixIcon } from '@repo/ui/icons'
import Modal from '@repo/ui/modal'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'

import { getCenter } from '../../../../api/centers/get-center'
import { getCenterBudget } from '../../../../api/centers/get-center-budget'
import useAuth from '../../../../lib/hooks/use-auth'
import { useDataProps } from '../../../../lib/hooks/use-data-props'
import { useModalManager } from '../../../../lib/hooks/use-modal-manager'
import useTranslate from '../../../../lib/hooks/use-translate'
import { CenterPartial } from '../../../../lib/types/models/center/center'
import { CenterBudgets } from '../../../../lib/types/models/center/center-budget'
import { DataProps, IdInput } from '../../../../lib/types/utils'
import EditableTable, { EditableTableColumn } from '../../../elements/editable-table'
import { useCenterCosts } from './center.hooks'
import {
  centerCostsTableColorMapping,
  CenterCostsTableFooter,
  CenterCostsTableHeader,
  CenterTableSkeleton,
  UploadInvoiceComponent,
  userInvoiceColorMapping
} from './center.ui'
import { CenterDetailedActivitiesTable } from './center-detailed-activites-table'
import { CenterCostsTableProps, getCenterFrenchLeaderCostsColumns } from './utils'

export function CenterFrenchLeaderCostsTable(
  props: DataProps<CenterPartial, CenterCostsTableProps>
) {
  const { mode, costType, currency } = props
  const { t } = useTranslate()
  const { user } = useAuth()
  const [editable, setEditable] = useState(false)

  const { id: centerId, data: center } = useDataProps(props, (id) => getCenter(id, 'PARTIAL'))

  const { isPending, uploadInvoice, deleteInvoice, lock, unlock } = useCenterCosts({
    centerId,
    costType,
    table: 'budget'
  })

  const { modalState, toggleModal, closeModel } = useModalManager<'detailed-view', number>([
    'detailed-view'
  ])

  const {
    data: centerBudget,
    isPending: isCenterBudegetPending,
    refetch: refetchCenterBudget
  } = useQuery({
    queryKey: ['center-budget', centerId],
    queryFn: () => getCenterBudget(centerId)
  })

  const columns: EditableTableColumn<IdInput<CenterBudgets>>[] = [
    ...getCenterFrenchLeaderCostsColumns(costType, currency, t),
    ...(costType === 'final-cost'
      ? ([
          {
            accessorKey: 'invoices',
            header: t('center.costs.tefl.teachers.and.leaders.table.header.upload.receipts'),
            centerCell: true,
            cell: ({ row }) => (
              <UploadInvoiceComponent
                variant={userInvoiceColorMapping[user.role]}
                label={t('center.costs.french.leader.budget.upload.invoice')}
                invoice={centerBudget?.centerBudgetInvoice}
                readOnly={(!editable && mode === 'action-edit') || mode === 'readonly'}
                disabled={isPending}
                onDelete={async () => {
                  if (isCenterBudegetPending) return
                  await deleteInvoice.mutateAsync(row.original.id)
                  refetchCenterBudget()
                }}
                onChange={async (file) => {
                  if (isCenterBudegetPending) return
                  await uploadInvoice.mutateAsync({ id: row.original.id, file })
                  refetchCenterBudget()
                }}
              />
            )
          }
        ] as EditableTableColumn<IdInput<CenterBudgets>>[])
      : [])
  ]

  if (!centerBudget || !center) return <CenterTableSkeleton />
  return (
    <div>
      <CenterCostsTableHeader
        mode={mode}
        icon={FeesSixIcon}
        title={t('center.costs.french.leader.budget.title')}
        {...(costType === 'original-cost' && {
          customButton: {
            label: 'Budget details',
            variant: 'outline',
            icon: CoinIcon,
            className: 'whitespace-nowrap',
            onClick: () => toggleModal('detailed-view')
          }
        })}
        onEditableChange={setEditable}
        validateOptions={{
          validate: costType === 'final-cost',
          locked: center.isBudgetLocked,
          onLock: lock.mutateAsync,
          onUnlock: unlock.mutateAsync
        }}
      />

      <EditableTable<IdInput<CenterBudgets>>
        isEditable={editable}
        data={[{ id: centerId, ...centerBudget }]}
        columns={columns}
        manualSorting={false}
        footerRowClassName={cn({ 'bg-white': costType === 'final-cost' })}
        footer={
          <CenterCostsTableFooter
            currency={currency}
            costType={costType}
            textColor={centerCostsTableColorMapping[user.role][costType].textColor}
            variant={centerCostsTableColorMapping[user.role][costType].variant}
            label={
              costType === 'final-cost'
                ? t('center.final.costs.fees')
                : t('center.costs.french.leader.budget.table.footer.label')
            }
            total={centerBudget.totalBudget}
            initialAmount={centerBudget.totalBudget}
            finalAmount={centerBudget.totalBudget}
            balance={0}
          />
        }
      />

      <Modal
        size={'large'}
        icon={() => (
          <CoinIcon
            filled
            color={tailwindColors.secondary.light.DEFAULT}
            color2={tailwindColors.secondary.DEFAULT}
          />
        )}
        title={t('center.costs.french.leader.budget.details.title')}
        isOpen={modalState['detailed-view']}
        onCancel={() => closeModel('detailed-view')}
        actionButtonsOptions={{ cancelText: 'Close' }}
      >
        <div className={'p-4'}>
          <CenterDetailedActivitiesTable
            currency={currency}
            centerId={centerId}
            costType={costType}
          />
        </div>
      </Modal>
    </div>
  )
}
